import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { featuredTopics } from '@/mocks/topics';
import { siteInfo } from '@/mocks/site';
import { formatHeat } from '@/utils/format';

export default function HeroLive() {
  const [index, setIndex] = useState(0);
  const total = featuredTopics.length;

  useEffect(() => {
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % total);
    }, 5200);
    return () => window.clearInterval(timer);
  }, [total]);

  const active = featuredTopics[index];

  return (
    <section className="relative w-full overflow-hidden" aria-labelledby="hero-title">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=warm%20abstract%20editorial%20background%20with%20layered%20coral%20red%20and%20amber%20gold%20paper%20shapes%20floating%20over%20a%20cream%20textured%20canvas%2C%20soft%20film%20grain%2C%20subtle%20geometric%20light%20rays%2C%20minimal%20modern%20graphic%20art%2C%20no%20text%2C%20balanced%20composition%2C%20high%20detail&width=1600&height=900&seq=hl-hero-01&orientation=landscape"
          alt="黑料首页首屏背景，暖色调抽象图形"
          className="h-full w-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/75 via-foreground-950/60 to-foreground-950/80" />
      </div>

      <div className="relative mx-auto grid max-w-shell grid-cols-1 items-center gap-10 px-4 py-16 md:px-6 md:py-20 lg:grid-cols-[1.05fr_1fr] lg:py-24">
        <div className="animate-fade-rise">
          <span className="inline-flex items-center gap-2 rounded-full border border-background-50/30 bg-background-50/10 px-3 py-1 text-xs font-medium text-background-50 backdrop-blur">
            <span className="flex h-3 w-3 items-center justify-center">
              <i className="ri-live-line text-primary-300" aria-hidden="true" />
            </span>
            首屏区高频刷新 · 最新动态持续上架
          </span>

          <h2
            id="hero-title"
            className="mt-5 font-heading text-3xl font-bold leading-tight tracking-tight text-background-50 md:text-5xl"
          >
            首屏新内容，实时上新
            <span className="mt-2 block text-xl font-semibold text-accent-300 md:text-2xl">
              最新动态与轮播更新，抢先看
            </span>
          </h2>

          <p className="mt-5 max-w-xl text-sm leading-relaxed text-background-100 md:text-base">
            {siteInfo.name}
            首屏区保持高频刷新，最新动态、轮播更新、新内容持续上架。不管你是蹲新瓜还是追后续，这里都能让你第一时间看到刚出炉的爆料与实锤，不错过任何一条新鲜猛料。
          </p>

          <div className="mt-7 flex flex-wrap items-center gap-3">
            <Link
              to="/topics"
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-compass-3-line" aria-hidden="true" />
              进入话题库
            </Link>
            <a
              href="#live-feed"
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/40 bg-background-50/10 px-5 py-2.5 text-sm font-medium text-background-50 backdrop-blur transition-colors hover:bg-background-50/20"
            >
              <i className="ri-flashlight-line" aria-hidden="true" />
              看正在发生
            </a>
          </div>

          <dl className="mt-9 grid grid-cols-2 gap-x-6 gap-y-5 sm:grid-cols-4">
            {siteInfo.stats.map((stat) => (
              <div key={stat.label}>
                <dt className="text-[11px] text-background-200">{stat.label}</dt>
                <dd className="mt-1 font-heading text-xl font-bold text-background-50 md:text-2xl">{stat.value}</dd>
              </div>
            ))}
          </dl>
        </div>

        <div className="animate-fade-rise">
          <div className="overflow-hidden rounded-lg border border-background-50/15 bg-background-50">
            <div className="flex items-center justify-between border-b border-background-200 px-4 py-2.5">
              <span className="flex items-center gap-2 text-xs font-medium text-foreground-800">
                <span className="flex h-2 w-2 items-center justify-center">
                  <span className="block h-2 w-2 rounded-full bg-primary-500 animate-live" />
                </span>
                轮播更新
              </span>
              <span className="text-[11px] text-foreground-600">
                {index + 1} / {total}
              </span>
            </div>

            {active && (
              <Link to={`/topic/${active.id}`} className="group block">
                <div className="relative h-44 w-full overflow-hidden bg-background-200 md:h-52">
                  <img
                    src={active.cover}
                    alt={active.coverAlt}
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute left-3 top-3 rounded-md bg-primary-500 px-2 py-0.5 text-[10px] font-medium text-background-50">
                    {active.isBreaking ? '突发事件' : '推荐'}
                  </span>
                </div>
                <div className="p-4">
                  <div className="flex flex-wrap items-center gap-2 text-[11px] text-foreground-600">
                    <span className="rounded-full bg-secondary-100 px-2 py-0.5 font-medium text-secondary-900">
                      {active.categoryName}
                    </span>
                    <span>更新于 {active.updatedAt.slice(5, 16)}</span>
                  </div>
                  <h3 className="mt-2 line-clamp-2 font-heading text-lg font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700">
                    {active.title}
                  </h3>
                  <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">{active.summary}</p>
                  <div className="mt-3 flex items-center justify-between text-[11px] text-foreground-600">
                    <span className="flex items-center gap-1 text-primary-700">
                      <i className="ri-fire-line" aria-hidden="true" />
                      热度 {formatHeat(active.heat)}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line" aria-hidden="true" />
                      {formatHeat(active.views)} 阅读
                    </span>
                  </div>
                </div>
              </Link>
            )}

            <div className="flex items-center gap-1.5 border-t border-background-200 px-4 py-3">
              {featuredTopics.map((item, i) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setIndex(i)}
                  aria-label={`切换到轮播第 ${i + 1} 条`}
                  className={`h-1.5 rounded-full transition-all ${
                    i === index ? 'w-6 bg-primary-500' : 'w-1.5 bg-background-300 hover:bg-background-400'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}